import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';

import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { useSelectedSymbols } from '@/features/DataHub/hooks/useSelectedSymbols';
import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import {
  useJsonCsvPreview,
  usePowerQueryPreview,
  usePythonPreview
} from '@/features/DataHub/modules/Export/hooks';
import {
  apiPreviewService,
  snowflakePreviewService
} from '@/features/DataHub/modules/Export/services';
import type { ExportOption, PreviewConfig } from '@/features/DataHub/modules/Export/types';
import { getFromToValues, scrambleSecrets } from '@/features/DataHub/modules/Export/utils';

import { DataLoader } from './DataLoader';
import { DocumentationInfo } from './DocumentationInfo';
import ExportPreviewScreen from './ExportPreviewScreen';
import { SyntaxHighlighter } from './SyntaxHighlighter';
import TokenLink from './TokenLink';

type UniversalPreviewRendererProps = {
  previewConfig: PreviewConfig | undefined;
  exportOption: ExportOption;
};

const UniversalPreviewRenderer = ({
  previewConfig,
  exportOption
}: UniversalPreviewRendererProps) => {
  const { capturePosthogEvent, exportSettings, exportApiRequestString, setPreviewLoading } =
    useExport();

  const symbols = useSelectedSymbols();
  const { timezoneValue } = useDataHubUserConfig();

  const { fromValue, toValue } = getFromToValues(exportSettings);

  const isPython = exportOption.id === 'python';
  const isPowerQuery = exportOption.id === 'power_query';
  const isCsvJson = exportOption.id === 'csv' || exportOption.id === 'curl';

  // Use appropriate hooks based on export type
  const pythonHook = usePythonPreview(isPython ? capturePosthogEvent : () => {}, isPython);

  const powerQueryHook = usePowerQueryPreview(
    isPowerQuery ? capturePosthogEvent : () => {},
    exportApiRequestString,
    isPowerQuery
  );
  const csvJsonHook = useJsonCsvPreview(
    exportOption.id === 'csv',
    isCsvJson ? capturePosthogEvent : () => {},
    isCsvJson
  );

  // Determine which hook data to use
  const getHookData = () => {
    switch (exportOption.id) {
      case 'python':
        return pythonHook;
      case 'power_query':
        return powerQueryHook;
      case 'csv':
      case 'curl':
        return csvJsonHook;
      default:
        return { loading: false, error: false, fileContent: '', downloadFile: () => {} };
    }
  };

  const { loading, error, fileContent, downloadFile } = getHookData();

  // Sync preview loading state with context
  useEffect(() => {
    setPreviewLoading(loading);
    return () => {
      setPreviewLoading(false);
    };
  }, [loading, setPreviewLoading]);

  // Sync download function with context for client-side downloads
  const { setPreviewDownloadFn } = useExport();
  useEffect(() => {
    if (downloadFile && fileContent) {
      setPreviewDownloadFn(() => downloadFile);
    }
    return () => {
      setPreviewDownloadFn(null);
    };
  }, [downloadFile, fileContent, setPreviewDownloadFn]);

  // Get preview content (unscrambled - will be scrambled only for display)
  const content = useMemo(() => {
    if (!previewConfig) return;
    if (previewConfig.fetchTemplate && fileContent) {
      // For fetched templates, use fileContent from hooks
      return fileContent;
    }

    // For generated content, use services
    switch (previewConfig.type) {
      case 'api':
        return exportApiRequestString;
      case 'code':
        if (exportOption.id === 'curl') {
          // Use API service to generate cURL command
          const serviceContext = {
            symbols,
            rangeStart: fromValue,
            rangeEnd: toValue,
            timezone: timezoneValue,
            includeMetadata: exportSettings.includeMetadata,
            apiKey: '<YourApiKeyHere>',
            gxURL: import.meta.env.VITE_GX_API_ENDPOINT
          };
          return apiPreviewService.generateCurlCommand(serviceContext);
        }
        return fileContent;
      case 'sql': {
        // Use Snowflake service to generate SQL query
        const sqlContext = {
          symbols,
          rangeStart: fromValue,
          rangeEnd: toValue,
          timezone: timezoneValue,
          includeMetadata: exportSettings.includeMetadata,
          apiKey: '',
          gxURL: ''
        };
        return snowflakePreviewService.generatePreview(sqlContext).content;
      }
      default:
        return '';
    }
  }, [
    previewConfig,
    fileContent,
    exportOption.id,
    exportApiRequestString,
    symbols,
    fromValue,
    toValue,
    timezoneValue,
    exportSettings.includeMetadata
  ]);

  // Render preview content based on type
  const renderContent = (): ReactNode => {
    if (error) {
      return (
        <Typography
          color='error'
          sx={{ mt: 2, fontSize: '14px' }}
        >
          An error occurred while fetching data. Please try again.
        </Typography>
      );
    }

    if (loading) {
      return <DataLoader />;
    }

    if (!previewConfig)
      return (
        <Box
          sx={{
            minWidth: 0,
            gap: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {exportOption.documentationType && (
            <DocumentationInfo type={exportOption.documentationType} />
          )}
        </Box>
      );

    switch (previewConfig.type) {
      case 'code':
      case 'sql':
      case 'api':
        return (
          <Box
            sx={{
              minWidth: 0,
              gap: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {exportOption.documentationType && (
              <DocumentationInfo type={exportOption.documentationType} />
            )}
            <SyntaxHighlighter
              fileContent={scrambleSecrets(content || '')}
              language={previewConfig.language || 'text'}
            />
          </Box>
        );

      case 'simple':
        return (
          <Box
            sx={{
              minWidth: 0,
              gap: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {exportOption.documentationType && (
              <DocumentationInfo type={exportOption.documentationType} />
            )}
            <SyntaxHighlighter
              fileContent={fileContent || ''}
              language={previewConfig.language || 'text'}
            />
          </Box>
        );

      case 'integration':
        return null;

      default:
        return null;
    }
  };

  const listFooter = exportOption.showTokenLink ? <TokenLink /> : undefined;

  return (
    <ExportPreviewScreen
      actionHeading={exportOption.actionHeading}
      bulletPoints={exportOption.bulletPoints}
      listFooter={listFooter}
    >
      {renderContent()}
    </ExportPreviewScreen>
  );
};

export default UniversalPreviewRenderer;
